import { css } from '@emotion/react';

export const markdownContentStyles = css`
  ul {
    margin: var(--spacing-3) 0;
    padding-left: var(--spacing-5);
    list-style-type: disc;
    list-style-position: outside;
  }

  ol {
    margin: var(--spacing-3) 0;
    padding-left: var(--spacing-5);
    list-style-type: decimal;
    list-style-position: outside;
  }

  /* Nested lists */
  ul ul {
    list-style-type: circle;
    margin: var(--spacing-2) 0;
  }

  ul ul ul {
    list-style-type: square;
  }

  ol ol {
    list-style-type: lower-alpha;
    margin: var(--spacing-2) 0;
  }

  ol ol ol {
    list-style-type: lower-roman;
  }

  li {
    margin: var(--spacing-2) 0;
    line-height: 1.6;
  }

  /* Ensure list markers are visible */
  li::marker {
    color: var(--text-primary);
  }

  blockquote {
    border-left: 3px solid var(--border-primary);
    margin: var(--spacing-4) 0;
    padding-left: var(--spacing-4);
    color: var(--text-primary);
    font-style: italic;
    line-height: 1.6;
  }

  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    margin: var(--spacing-4) 0;
    border-radius: var(--radius-lg);
    overflow: hidden;
    font-size: 0.875rem;
    border: 1px solid var(--border-primary);

    @media (max-width: 768px) {
      font-size: 0.75rem;
      border-radius: var(--radius-md);
      min-width: 100%;
      margin: var(--spacing-2) 0;
    }
  }

  th,
  td {
    border-right: 1px solid var(--border-primary);
    border-bottom: 1px solid var(--border-primary);
    padding: var(--spacing-3);
    text-align: left;
    min-width: var(--spacing-24);

    &:last-child {
      border-right: none;
    }

    @media (max-width: 768px) {
      padding: var(--spacing-2);
    }
  }

  thead tr:first-child th:first-child {
    border-top-left-radius: var(--radius-lg);

    @media (max-width: 768px) {
      border-top-left-radius: var(--radius-md);
    }
  }

  thead tr:first-child th:last-child {
    border-top-right-radius: var(--radius-lg);

    @media (max-width: 768px) {
      border-top-right-radius: var(--radius-md);
    }
  }

  tbody tr:last-child td:first-child {
    border-bottom-left-radius: var(--radius-lg);

    @media (max-width: 768px) {
      border-bottom-left-radius: var(--radius-md);
    }
  }

  tbody tr:last-child td:last-child {
    border-bottom-right-radius: var(--radius-lg);

    @media (max-width: 768px) {
      border-bottom-right-radius: var(--radius-md);
    }
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  th {
    background-color: var(--surface-neutral);
    font-weight: 600;
  }

  /* Scope link styles to prose content only, excluding component-based links */
  p a,
  li a,
  blockquote a,
  td a,
  th a {
    color: var(--color-primary-500);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }
`;

export const codeBlockStyles = css`
  position: relative;
  margin: var(--spacing-4) 0;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-primary);
  overflow: hidden;
`;

export const codeBlockHeaderStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-2) var(--spacing-3);
  background-color: var(--surface-neutral);
  border-bottom: 1px solid var(--border-primary);
  position: relative;
`;

export const codeBlockLanguageStyles = css`
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  font-weight: 500;
`;

export const getCodeBlockCopyButtonStyles = (
  isHovered: boolean,
  copySuccess: boolean
) => css`
  position: absolute;
  top: 50%;
  right: var(--spacing-3);
  transform: translateY(-50%);
  background: ${copySuccess
    ? 'var(--color-success-500)'
    : 'var(--surface-action)'};
  border: none;
  border-radius: var(--radius-md);
  padding: var(--spacing-1) var(--spacing-2);
  color: var(--text-on-action);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${isHovered ? 1 : 0};
  pointer-events: ${isHovered ? 'auto' : 'none'};

  &:hover {
    background: ${copySuccess
      ? 'var(--color-success-600)'
      : 'var(--surface-action-hover)'};
  }
`;

export const codeBlockPreStyles = css`
  margin: 0;
  padding: var(--spacing-3);
  background-color: var(--surface-page);
  color: var(--text-primary);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro',
    monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
`;

export const inlineCodeStyles = css`
  background-color: var(--surface-neutral);
  color: var(--text-primary);
  font-weight: 500;
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--radius-md);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro',
    monospace;
  font-size: 0.9em;
`;

export const tableWrapperStyles = css`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    margin: var(--spacing-4) calc(-1 * var(--spacing-3));
    padding: 0 var(--spacing-3);
  }
`;
